import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../style/Admin.css';

const API = process.env.REACT_APP_API_URL || '';

const EMPTY_FORM = {
    clientName:  '',
    designation: '',
    company:     '',
    message:     '',
    avatarUrl:   '',
    status:      'ACTIVE'
};

export default function AdminTestimonialForm() {
    const [form, setForm]     = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState('');
    const { token, logout }   = useAdminAuth();
    const navigate            = useNavigate();
    const { id }              = useParams();
    const isEdit              = !!id;

    useEffect(() => {
        if (!isEdit) return;
        fetch(`${API}/api/admin/testimonials/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => {
            if (res.status === 401) { logout(); navigate('/admin/login'); return null; }
            return res.json();
        })
        .then(data => { if (data) setForm(data); })
        .catch(() => setError('Failed to load testimonial.'));
    }, [id]); // eslint-disable-line

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const url    = isEdit ? `${API}/api/admin/testimonials/${id}` : `${API}/api/admin/testimonials`;
            const method = isEdit ? 'PUT' : 'POST';
            const res    = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization:  `Bearer ${token}`
                },
                body: JSON.stringify(form)
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (res.ok) {
                navigate('/admin/dashboard?tab=testimonials');
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to save testimonial.');
            }
        } catch {
            setError('Cannot connect to server.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-layout">
            <header className="admin-header">
                <div className="admin-header-left">
                    <span className="admin-header-logo">KOVAN Admin</span>
                </div>
                <div className="admin-header-right">
                    <button className="admin-btn-outline" onClick={() => navigate('/admin/dashboard?tab=testimonials')}>
                        ← Back to Dashboard
                    </button>
                </div>
            </header>

            <main className="admin-main">
                <h1 className="admin-page-title">{isEdit ? 'Edit Testimonial' : 'Add Testimonial'}</h1>

                {error && <div className="admin-error">{error}</div>}

                <form onSubmit={handleSubmit} className="admin-job-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <label>Client Name *</label>
                            <input
                                name="clientName"
                                value={form.clientName}
                                onChange={handleChange}
                                placeholder="e.g. Sarah Jenkins"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Designation *</label>
                            <input
                                name="designation"
                                value={form.designation}
                                onChange={handleChange}
                                placeholder="e.g. CTO"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Company *</label>
                            <input
                                name="company"
                                value={form.company}
                                onChange={handleChange}
                                placeholder="e.g. FinTech Corp"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Avatar Image URL</label>
                            <input
                                name="avatarUrl"
                                value={form.avatarUrl}
                                onChange={handleChange}
                                placeholder="https://example.com/photo.jpg"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Status *</label>
                            <select name="status" value={form.status} onChange={handleChange} required>
                                <option value="ACTIVE">Active (visible on Homepage)</option>
                                <option value="DRAFT">Draft (hidden from public)</option>
                            </select>
                        </div>
                    </div>

                    {form.avatarUrl && (
                        <div className="admin-form-group" style={{ marginBottom: '1.5rem' }}>
                            <label>Image Preview</label>
                            <img
                                src={form.avatarUrl}
                                alt="Avatar preview"
                                style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}

                    <div className="admin-form-group admin-form-full">
                        <label>Testimonial Message *</label>
                        <textarea
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            placeholder="What did the client say about Kovan?"
                            rows={5}
                            required
                        />
                    </div>

                    <div className="admin-form-actions">
                        <button type="submit" className="admin-btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Testimonial')}
                        </button>
                        <button
                            type="button"
                            className="admin-btn-outline"
                            onClick={() => navigate('/admin/dashboard?tab=testimonials')}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}
